import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkle } from "./BrandLogo";

/**
 * Quiet wall of dedications. Never shows amounts, counts, or "raised so far".
 * Each card is a small piece of paper pinned to the wall.
 */
export default function HonorWall() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/donations/wall").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);

  if (items === null) {
    return <div className="rounded-3xl bg-card border border-border p-6 h-48 animate-soft-pulse" />;
  }
  if (items.length === 0) return null;

  // Subtle paper tilt + warm hues for variety
  const tilts = ["-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.75deg]", "rotate-0"];
  const tones = [
    { bg: "bg-[hsl(335_85%_97%)]", edge: "border-[hsl(335_60%_88%)]" },
    { bg: "bg-[hsl(36_60%_96%)]",  edge: "border-[hsl(36_40%_88%)]" },
    { bg: "bg-[hsl(320_30%_96%)]", edge: "border-[hsl(320_20%_88%)]" },
    { bg: "bg-[hsl(0_0%_99%)]",    edge: "border-[hsl(335_40%_90%)]" },
  ];

  return (
    <section data-testid="honor-wall" className="relative">
      <div className="flex items-center gap-2">
        <Sparkle className="w-3 h-3 text-brand" />
        <p className="font-script text-2xl text-brand">the quiet wall</p>
      </div>
      <h3 className="font-serif text-3xl leading-tight mt-1">
        Held in <em>honor</em>.
      </h3>
      <p className="mt-2 text-sm text-foreground/65 leading-relaxed">
        Names and notes left by people who gave. No amounts. No counts. Just memory.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((it, i) => {
          const tone = tones[i % tones.length];
          const tilt = tilts[i % tilts.length];
          return (
            <article
              key={i}
              data-testid={`honor-card-${i}`}
              className={`relative ${tone.bg} ${tone.edge} border rounded-[22px] p-5 shadow-[0_6px_18px_-12px_rgba(157,31,90,0.25)] ${tilt} transition-transform hover:rotate-0`}
            >
              <span className="absolute -top-2 left-6 w-8 h-3 rounded-full bg-brand/20 blur-[2px]" aria-hidden="true" />
              <p className="font-script text-2xl text-brand leading-none">
                in honor of
              </p>
              <p className="font-serif text-2xl italic mt-1 leading-snug text-foreground">
                {it.honoree_name}
              </p>
              {it.dedication_note && (
                <p className="mt-3 font-serif italic text-[15px] leading-relaxed text-foreground/80">
                  “{it.dedication_note}”
                </p>
              )}
              <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-foreground/45">
                {it.donor_label || "from a friend"}
              </p>
            </article>
          );
        })}
      </div>
      <p className="mt-6 text-center text-xs text-foreground/45 italic">
        Add your own when you give — privately, or quietly here on the wall.
      </p>
    </section>
  );
}
